import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Filter, ShoppingCart, Eye, Edit2, Trash2,
  UserPlus, Clock, AlertCircle, Download, FileText, Package, Settings, FolderPlus, X, Copy
} from 'lucide-react';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

interface Consumable {
  id: number;
  category_id: number;
  category_name: string;
  stock_code: string;
  name: string;
  brand: string;
  model: string;
  unit_price: number;
  total_quantity: number;
  available_quantity: number;
  minimum_stock: number;
  location_name: string;
  purchase_date: string;
  notes: string;
  photo_path: string;
  is_low_stock: boolean;
  created_at: string;
  created_by_username: string;
}

interface Category {
  id: number;
  name: string;
  description: string;
}

interface Location {
  id: number;
  name: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
}

interface Printer {
  id: number;
  name: string;
  brand: string;
  model: string;
  location: string;
}

const ConsumablesPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [consumables, setConsumables] = useState<Consumable[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showQuickCategoryModal, setShowQuickCategoryModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [stockData, setStockData] = useState({
    quantity: '',
    supplier_id: '',
    unit_price: '',
    notes: '',
    purchase_date: new Date().toISOString().split('T')[0]
  });
  const [suppliers, setSuppliers] = useState([]);
  const [selectedConsumable, setSelectedConsumable] = useState<Consumable | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [photoUploading, setPhotoUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    brand: '',
    model: '',
    unit_price: '',
    total_quantity: '',
    minimum_stock: '',
    location_id: '',
    purchase_date: '',
    notes: '',
    photo_path: ''
  });

  const [assignData, setAssignData] = useState({
    user_id: '',
    printer_id: '',
    quantity: '',
    notes: ''
  });

  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: ''
  });
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);

  useEffect(() => {
    fetchData();
    fetchUsers();
    fetchPrinters();
    fetchSuppliers();
  }, [currentPage, selectedCategory, showLowStock, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(selectedCategory && { category_id: selectedCategory }),
        ...(showLowStock && { low_stock: 'true' }),
        ...(searchTerm && { search: searchTerm })
      });

      const [consumablesRes, categoriesRes, locationsRes] = await Promise.all([
        fetch(`/api/consumables?${params}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch('/api/consumables/categories', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch('/api/locations', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        })
      ]);

      if (consumablesRes.ok && categoriesRes.ok && locationsRes.ok) {
        const consumablesData = await consumablesRes.json();
        const categoriesData = await categoriesRes.json();
        const locationsData = await locationsRes.json();

        setConsumables(Array.isArray(consumablesData.data) ? consumablesData.data : []);
        setCategories(Array.isArray(categoriesData.data) ? categoriesData.data : []);
        setLocations(Array.isArray(locationsData) ? locationsData : Array.isArray(locationsData.data) ? locationsData.data : []);
        setTotalPages(consumablesData.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : []);
      }
    } catch (error) {
      console.error('Kullanıcılar yükleme hatası:', error);
    }
  };

  const fetchPrinters = async () => {
    try {
      const response = await fetch('/api/printers', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrinters(Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : []);
      }
    } catch (error) {
      console.error('Yazıcılar yükleme hatası:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetch('/api/suppliers', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuppliers(Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : []);
      }
    } catch (error) {
      console.error('Tedarikçiler yükleme hatası:', error);
    }
  };

  const fetchHistory = async (consumableId: number) => {
    try {
      const response = await fetch(`/api/consumables/${consumableId}/history`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHistoryData(Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : []);
      }
    } catch (error) {
      console.error('Geçmiş yükleme hatası:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Upload photo first if selected
      let photoPath = '';
      if (selectedPhoto) {
        photoPath = await uploadPhoto() || '';
      }

      const submitData = { ...formData, photo_path: photoPath };

      const url = selectedConsumable ? `/api/consumables/${selectedConsumable.id}` : '/api/consumables';
      const method = selectedConsumable ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({
          category_id: '', name: '', brand: '', model: '', unit_price: '',
          total_quantity: '', minimum_stock: '', location_id: '',
          purchase_date: '', notes: '', photo_path: ''
        });
        setSelectedPhoto(null);
        setPhotoPreview('');
        fetchData();
        
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            message: t('consumables.successAdded')
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            message: errorData.error || t('consumables.errSave')
          }
        }));
      }
    } catch (error) {
      console.error('Sarf malzeme ekleme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: language === 'en' ? 'Connection error' : 'Bağlantı hatası'
        }
      }));
    }
  };

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!selectedPhoto) return null;

    const photoFormData = new FormData();
    photoFormData.append('photo', selectedPhoto);

    setPhotoUploading(true);
    try {
      const response = await fetch('/api/upload/equipment-photo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: photoFormData
      });

      if (response.ok) {
        const result = await response.json();
        return result.data.path;
      } else {
        throw new Error('Fotoğraf yükleme başarısız');
      }
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedConsumable) return;
    
    try {
      // Veriyi backend için hazırla
      const requestData = {
        quantity: parseInt(assignData.quantity),
        notes: assignData.notes
      };
      
      // Toner ise printer_id, değilse user_id ekle
      if (selectedConsumable.category_name === 'Toner' && String(selectedConsumable.name || '').toLowerCase().includes('toner')) {
        if (assignData.printer_id) {
          requestData.printer_id = parseInt(assignData.printer_id);
        }
      } else {
        if (assignData.user_id) {
          requestData.user_id = parseInt(assignData.user_id);
        }
      }

      const response = await fetch(`/api/consumables/${selectedConsumable.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        setShowAssignModal(false);
        setAssignData({ user_id: '', printer_id: '', quantity: '', notes: '' });
        fetchData();
        
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            message: result.message || t('consumables.successAssigned')
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            message: errorData.error || t('consumables.errAssign')
          }
        }));
      }
    } catch (error) {
      console.error('Zimmetleme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: language === 'en' ? 'Connection error' : 'Bağlantı hatası'
        }
      }));
    }
  };

  const generateStockReport = async () => {
    try {
      const response = await fetch('/api/consumables/stock-report', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Excel dosyası oluşturmak için basit bir CSV format
        const csvContent = [
          ['Malzeme Adı', 'Kategori', 'Marka', 'Model', 'Toplam Miktar', 'Mevcut Miktar', 'Minimum Stok', 'Durum'].join(','),
          ...data.data.map((item: any) => [
            item.name,
            item.category_name,
            item.brand || '',
            item.model || '',
            item.total_quantity,
            item.available_quantity,
            item.minimum_stock,
            item.stock_status
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `stok_raporu_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Stok raporu oluşturma hatası:', error);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/consumables/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(categoryFormData)
      });

      if (response.ok) {
        setShowCategoryModal(false);
        setShowQuickCategoryModal(false);
        setCategoryFormData({ name: '', description: '' });
        fetchData(); // Kategorileri yeniden yükle
        
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            message: language === 'en' ? 'Category added successfully' : 'Kategori başarıyla eklendi'
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            message: errorData.error || 'Kategori eklenirken hata oluştu'
          }
        }));
      }
    } catch (error) {
      console.error('Kategori ekleme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: language === 'en' ? 'Connection error' : 'Bağlantı hatası'
        }
      }));
    }
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || ''
    });
    setShowEditCategoryModal(true);
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory) return;

    try {
      const response = await fetch(`/api/consumables/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(categoryFormData)
      });

      if (response.ok) {
        setShowEditCategoryModal(false);
        setEditingCategory(null);
        setCategoryFormData({ name: '', description: '' });
        fetchData(); // Kategorileri yeniden yükle
        
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            title: 'Başarılı',
            message: 'Kategori başarıyla güncellendi.'
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            title: 'Hata',
            message: errorData.message || 'Kategori güncellenirken bir hata oluştu.'
          }
        }));
      }
    } catch (error) {
      console.error('Kategori güncelleme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          title: 'Hata',
          message: 'Kategori güncellenirken bir hata oluştu.'
        }
      }));
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!confirm(language === 'en' ? 'Are you sure you want to delete this category?' : 'Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(`/api/consumables/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        fetchData(); // Kategorileri yeniden yükle
        
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            title: 'Başarılı',
            message: 'Kategori başarıyla silindi.'
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            title: 'Hata',
            message: errorData.message || 'Kategori silinirken bir hata oluştu.'
          }
        }));
      }
    } catch (error) {
      console.error('Kategori silme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          title: 'Hata',
          message: 'Kategori silinirken bir hata oluştu.'
        }
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('consumables.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
            {t('consumables.subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:space-x-3 gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowCategoryModal(true)}
            className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors min-h-[44px] text-sm"
          >
            <Settings className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Categories' : 'Kategoriler'}
          </button>
          <button
            onClick={generateStockReport}
            className="flex items-center justify-center px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors min-h-[44px] text-sm"
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Stock Report' : 'Stok Raporu'}
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('consumables.newConsumable')}
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 sm:w-5 sm:h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('consumables.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 text-base w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 min-h-[44px]"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 min-h-[44px]"
          >
            <option value="">{t('consumables.allCategories')}</option>
            {(categories || []).map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <div className="flex items-center min-h-[44px]">
            <input
              type="checkbox"
              id="lowStock"
              checked={showLowStock}
              onChange={(e) => setShowLowStock(e.target.checked)}
              className="mr-2 w-4 h-4"
            />
            <label htmlFor="lowStock" className="text-sm text-gray-700 dark:text-gray-300">
              {language === 'en' ? 'Low Stock Only' : 'Sadece Düşük Stok'}
            </label>
          </div>

          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('');
              setShowLowStock(false);
              setCurrentPage(1);
            }}
            className="flex items-center justify-center px-4 py-3 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 min-h-[44px] text-sm"
          >
            <Filter className="w-4 h-4 mr-2" />
            Temizle
          </button>
        </div>
      </div>

      {/* Sarf Malzeme Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Photo' : 'Fotoğraf'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {t('consumables.title')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Category' : 'Kategori'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Stock' : 'Stok Durumu'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Unit Price' : 'Birim Fiyat'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Location' : 'Lokasyon'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Actions' : 'İşlemler'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-600">
              {consumables.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.photo_path ? (
                      <img 
                        src={item.photo_path} 
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          setSelectedPhotoUrl(item.photo_path);
                          setShowPhotoModal(true);
                        }}
                        title="Fotoğrafı büyütmek için tıklayın"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <ShoppingCart className="w-8 h-8 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {item.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {item.brand && item.model ? `${item.brand} ${item.model}` : item.brand || t('consumables.brandNotSet')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.category_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {item.available_quantity} / {item.total_quantity}
                      </div>
                      {item.is_low_stock && (
                        <AlertCircle className="w-4 h-4 text-red-500 ml-2" title="Düşük stok" />
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Min: {item.minimum_stock}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.unit_price ? `₺${Number(item.unit_price).toFixed(2)}` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.location_name || 'Belirtilmemiş'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedConsumable(item);
                          fetchHistory(item.id);
                          setShowHistoryModal(true);
                        }}
                        className="text-primary hover:text-primary-dark dark:text-primary dark:hover:text-accent"
                        title="Geçmiş"
                      >
                        <Clock className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => {
                          setSelectedConsumable(item);
                          setStockData({ 
                            quantity: '', 
                            supplier_id: '', 
                            unit_price: '', 
                            notes: '',
                            purchase_date: new Date().toISOString().split('T')[0]
                          });
                          setShowStockModal(true);
                        }}
                        className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                        title="Stok Ekle"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      
                      {item.available_quantity > 0 && (
                        <button
                          onClick={() => {
                            setSelectedConsumable(item);
                            setShowAssignModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                          title="Zimmetle"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                      )}

                      <button
                        onClick={() => {
                          // Kopyalama: Görsel hariç tüm bilgileri kopyala
                          setSelectedConsumable(null); // Yeni kayıt olarak işaretle
                          setFormData({
                            category_id: item.category_id.toString(),
                            name: item.name,
                            brand: item.brand || '',
                            model: item.model || '',
                            unit_price: item.unit_price?.toString() || '',
                            total_quantity: '0', // Stok sıfırdan başlasın
                            minimum_stock: item.minimum_stock?.toString() || '',
                            location_id: item.location_id?.toString() || '',
                            purchase_date: new Date().toISOString().split('T')[0], // Bugünün tarihi
                            notes: item.notes || '',
                            photo_path: '' // Görsel kopyalanmaz
                          });
                          setPhotoPreview(''); // Fotoğraf önizlemesini temizle
                          setSelectedPhoto(null); // Seçili fotoğrafı temizle
                          setShowModal(true);
                        }}
                        className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                        title="Kopyala"
                      >
                        <Copy className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => {
                          setSelectedConsumable(item);
                          setFormData({
                            category_id: item.category_id.toString(),
                            name: item.name,
                            brand: item.brand || '',
                            model: item.model || '',
                            unit_price: item.unit_price?.toString() || '',
                            total_quantity: item.total_quantity.toString(),
                            minimum_stock: item.minimum_stock?.toString() || '',
                            location_id: item.location_id?.toString() || '',
                            purchase_date: item.purchase_date || '',
                            notes: item.notes || '',
                            photo_path: item.photo_path || ''
                          });
                          setShowModal(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        title="Düzenle"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => {
                          if (confirm('Bu sarf malzemeyi silmek istediğinizden emin misiniz?')) {
                            fetch(`/api/consumables/${item.id}`, {
                              method: 'DELETE',
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                              }
                            }).then(() => {
                              fetchData();
                              window.dispatchEvent(new CustomEvent('showNotification', {
                                detail: { type: 'success', message: 'Sarf malzeme silindi' }
                              }));
                            });
                          }
                        }}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Sayfalama */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50"
              >
                Önceki
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50"
              >
                Sonraki
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Sayfa <span className="font-medium">{currentPage}</span> / <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary/10 border-primary text-primary'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Yeni Sarf Malzeme Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[85vh] flex flex-col">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex-shrink-0">
              {selectedConsumable ? t('consumables.editTitle') : t('consumables.newTitle')}
            </h3>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto px-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kategori *
                  </label>
                  <div className="flex space-x-2">
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                      required
                    >
                      <option value="">{t('consumables.selectCategory')}</option>
                      {(categories || []).map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowQuickCategoryModal(true)}
                      className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                      title="Hızlı Kategori Ekle"
                    >
                      <FolderPlus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Malzeme Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Marka
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Model
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Birim Fiyat
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Toplam Miktar *
                  </label>
                  <input
                    type="number"
                    value={formData.total_quantity}
                    onChange={(e) => setFormData({ ...formData, total_quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Minimum Stok
                  </label>
                  <input
                    type="number"
                    value={formData.minimum_stock}
                    onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lokasyon
                  </label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">{t('consumables.selectLocation')}</option>
                    {(locations || []).map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Alış Tarihi
                  </label>
                  <input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fotoğraf
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
                
                {/* Mevcut fotoğraf */}
                {selectedConsumable && selectedConsumable.photo_path && !photoPreview && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Mevcut fotoğraf:</p>
                    <img 
                      src={selectedConsumable.photo_path} 
                      alt="Mevcut fotoğraf" 
                      className="w-32 h-32 object-cover rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-80"
                      onClick={() => {
                        setSelectedPhotoUrl(selectedConsumable.photo_path);
                        setShowPhotoModal(true);
                      }}
                    />
                  </div>
                )}
                
                {/* Yeni fotoğraf önizlemesi */}
                {photoPreview && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Yeni fotoğraf önizlemesi:</p>
                    <img 
                      src={photoPreview} 
                      alt="Önizleme" 
                      className="w-32 h-32 object-cover rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-80"
                      onClick={() => {
                        setSelectedPhotoUrl(photoPreview);
                        setShowPhotoModal(true);
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notlar
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedConsumable(null);
                    setFormData({
                      category_id: '', name: '', brand: '', model: '', unit_price: '',
                      total_quantity: '', minimum_stock: '', location_id: '',
                      purchase_date: '', notes: '', photo_path: ''
                    });
                    setSelectedPhoto(null);
                    setPhotoPreview('');
                    setSelectedPhotoUrl('');
                    setShowPhotoModal(false);
                  }}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px]"
                >
                  {t('consumables.cancel')}
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-primary text-white rounded-md hover:bg-primary-dark min-h-[44px]"
                >
                  {selectedConsumable ? t('consumables.update') : t('consumables.save')}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Zimmetleme Modal */}
      <Modal isOpen={showAssignModal} onClose={() => setShowAssignModal(false)}>
          {selectedConsumable && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              {selectedConsumable.category_name === 'Toner' && String(selectedConsumable.name || '').toLowerCase().includes('toner') 
                ? 'Toner Takma' 
                : 'Sarf Malzeme Zimmetleme'}
            </h3>
            
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>{selectedConsumable.name}</strong>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Mevcut Stok: {selectedConsumable.available_quantity} adet
              </p>
            </div>
            
            <form onSubmit={handleAssign}>
              {/* Kategori kontrolü - Toner ise yazıcı seçimi, değilse kullanıcı seçimi */}
              {selectedConsumable.category_name === 'Toner' && String(selectedConsumable.name || '').toLowerCase().includes('toner') ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yazıcı Seçin *
                  </label>
                  <select
                    value={assignData.printer_id}
                    onChange={(e) => setAssignData({ ...assignData, printer_id: e.target.value, user_id: '' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                    required
                  >
                    <option value="">Yazıcı Seçin</option>
                    {(printers || []).map(printer => (
                      <option key={printer.id} value={printer.id}>
                        {printer.name} - {printer.brand} {printer.model} ({printer.location})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Bu toner yazıcıya takılacak
                  </p>
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kullanıcı *
                  </label>
                  <select
                    value={assignData.user_id}
                    onChange={(e) => setAssignData({ ...assignData, user_id: e.target.value, printer_id: '' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                    required
                  >
                    <option value="">Kullanıcı Seçin</option>
                    {(users || []).map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Miktar *
                </label>
                <input
                  type="number"
                  min="1"
                  max={selectedConsumable.available_quantity}
                  value={assignData.quantity}
                  onChange={(e) => setAssignData({ ...assignData, quantity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notlar
                </label>
                <textarea
                  value={assignData.notes}
                  onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedConsumable(null);
                    setAssignData({ user_id: '', printer_id: '', quantity: '', notes: '' });
                  }}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px]"
                >
                  {t('consumables.cancel')}
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-primary text-white rounded-md hover:bg-primary-dark min-h-[44px]"
                >
                  {selectedConsumable.category_name === 'Toner' && String(selectedConsumable.name || '').toLowerCase().includes('toner')
                    ? 'Tak'
                    : 'Zimmetle'}
                </button>
              </div>
            </form>
          </div>
          )}
        </Modal>

      {/* Kategori Yönetimi Modal */}
      <Modal isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Sarf Malzeme Kategorileri
              </h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4">
              <form onSubmit={handleCategorySubmit} className="flex space-x-2">
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  placeholder="Kategori adı"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
                <input
                  type="text"
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  placeholder="Açıklama (opsiyonel)"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                >{language === "en" ? "Add" : "Ekle"}</button>
              </form>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(categories || []).map((category) => (
                <div key={category.id} className="border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg p-4 relative group">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{category.name}</h4>
                      {category.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{category.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEditCategory(category)}
                        className="p-1 text-primary hover:text-primary-dark hover:bg-primary/10 rounded"
                        title="Düzenle"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Sil"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>

      {/* Kategori Düzenleme Modal */}
      <Modal isOpen={showEditCategoryModal} onClose={() => setShowEditCategoryModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Kategori Düzenle
            </h3>
            
            <form onSubmit={handleUpdateCategory}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kategori Adı *
                </label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditCategoryModal(false);
                    setEditingCategory(null);
                    setCategoryFormData({ name: '', description: '' });
                  }}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px]"
                >
                  {t('consumables.cancel')}
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-primary text-white rounded-md hover:bg-primary-dark min-h-[44px]"
                >
                  {t('consumables.update')}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Hızlı Kategori Ekleme Modal */}
      <Modal isOpen={showQuickCategoryModal} onClose={() => setShowQuickCategoryModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              {language === 'en' ? 'Quick Add Category' : 'Hızlı Kategori Ekle'}
            </h3>
            
            <form onSubmit={handleCategorySubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kategori Adı *
                </label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickCategoryModal(false);
                    setCategoryFormData({ name: '', description: '' });
                  }}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px]"
                >
                  {t('consumables.cancel')}
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-primary text-white rounded-md hover:bg-primary-dark min-h-[44px]"
                >{language === "en" ? "Add" : "Ekle"}</button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Stok Ekleme Modal */}
      <Modal isOpen={showStockModal} onClose={() => setShowStockModal(false)}>
          {selectedConsumable && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              {language === 'en' ? 'Add Stock' : 'Stok Ekleme'} - {selectedConsumable.name}
            </h3>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const response = await fetch(`/api/consumables/${selectedConsumable.id}/add-stock`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                  },
                  body: JSON.stringify(stockData)
                });
                
                if (response.ok) {
                  setShowStockModal(false);
                  fetchData();
                  window.dispatchEvent(new CustomEvent('showNotification', {
                    detail: { type: 'success', message: 'Stok başarıyla eklendi' }
                  }));
                }
              } catch (error) {
                console.error('Stok ekleme hatası:', error);
              }
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Miktar *
                </label>
                <input
                  type="number"
                  min="1"
                  value={stockData.quantity}
                  onChange={(e) => setStockData({ ...stockData, quantity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tedarikçi
                </label>
                <select
                  value={stockData.supplier_id}
                  onChange={(e) => setStockData({ ...stockData, supplier_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">Tedarikçi Seçin</option>
                  {(suppliers || []).map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Alış Tarihi *
                </label>
                <input
                  type="date"
                  value={stockData.purchase_date}
                  onChange={(e) => setStockData({ ...stockData, purchase_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Birim Fiyat
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={stockData.unit_price}
                  onChange={(e) => setStockData({ ...stockData, unit_price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Opsiyonel"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notlar
                </label>
                <textarea
                  value={stockData.notes}
                  onChange={(e) => setStockData({ ...stockData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Opsiyonel - Stok ekleme ile ilgili notlar..."
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setShowStockModal(false)}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px]"
                >
                  {t('consumables.cancel')}
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 min-h-[44px]"
                >
                  {language === 'en' ? 'Add Stock' : 'Stok Ekle'}
                </button>
              </div>
            </form>
          </div>
          )}
        </Modal>

      {/* Fotoğraf Modal */}
      {showPhotoModal && selectedPhotoUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[70]" onClick={() => setShowPhotoModal(false)}>
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setShowPhotoModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={selectedPhotoUrl} 
              alt="Büyütülmüş fotoğraf"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Geçmiş Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedConsumable && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {selectedConsumable.name} - Geçmiş
              </h3>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedConsumable(null);
                  setHistoryData([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      Tarih
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      İşlem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      Miktar
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      Kullanıcı/Yazıcı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      İşlemi Yapan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      Notlar
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {historyData.length > 0 ? (
                    historyData.map((record: any, index: number) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {new Date(record.assigned_date || record.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {record.printer_id ? 'Toner Takma' : 'Zimmetleme'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {record.quantity} adet
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {record.printer_id ? 
                            (record.printer_name || `Yazıcı ID: ${record.printer_id}`) : 
                            record.user_name || 'Bilinmeyen'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {record.assigned_by_name || 'Bilinmeyen'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {record.notes || '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        Henüz geçmiş kaydı bulunmuyor.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default ConsumablesPage;